// Payment Tracking — client-side CSV export of a single waterfall allocation
// trace: the header summary as key/value rows, then the full priority table
// with every line (fired, zero, skipped — never elided, matching the drawer).
// No backend roundtrip; RFC4180-quoted with an Excel-safe BOM, reusing the
// deal-pipeline exporter. Presentation only — amounts are raw numbers so the
// spreadsheet can do its own math, and dates are the raw wire instant for the
// same reason: a localised string drops the offset, so nothing downstream can
// reconstruct the moment. That means `Payment date` here does NOT match the
// day the drawer shows for an evening payment, which is deliberate and is
// written up at length in `@/components/deals/csv`.

import { downloadCsvFile, quote } from "@/components/deals/csv";
import type { ServicingAllocationDto } from "@/api/portfolioAdmin/servicingApi";
import { settlementLabel, TRACE_STATUS_LABEL } from "./servicingFormat";

const LINE_HEADERS = [
  "P",
  "Obligation",
  "Reason",
  "Recipient",
  "Amount",
  "Obligation after",
  "Settles via",
  "Status",
];

function row(cells: Array<string | number | null | undefined>): string {
  return cells.map(quote).join(",");
}

export function buildAllocationCsv(dto: ServicingAllocationDto): string {
  const summary = [
    row(["Waterfall allocation"]),
    row(["Payment type", dto.paymentType.replaceAll("_", " ")]),
    row(["Status", dto.status]),
    row(["Payment date (UTC)", dto.paymentDate]),
    row(["Amount", dto.amount]),
    row(["Interest portion", dto.interestPortion]),
    row(["Principal portion", dto.principalPortion]),
    row(["Balance after", dto.principalBalanceAfter]),
    row(["Distributed", dto.totalDistributed]),
    row(["Residual", dto.residual]),
  ];

  const table = [
    row(LINE_HEADERS),
    ...dto.lines.map((l) =>
      row([
        l.position,
        l.obligationTypeDisplay ?? l.obligationTypeCode,
        l.reason ?? "",
        l.recipientName ?? "",
        l.amount,
        l.obligationBalanceAfter,
        settlementLabel(l.settlementChannel),
        TRACE_STATUS_LABEL[l.status],
      ]),
    ),
  ];

  // One BOM at the very top (each section is already RFC4180-quoted).
  return `﻿${[...summary, "", ...table].join("\n")}`;
}

export function downloadAllocationCsv(dto: ServicingAllocationDto): void {
  const stamp = dto.paymentDate.slice(0, 10);
  downloadCsvFile(`allocation-${stamp}.csv`, buildAllocationCsv(dto));
}
